import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Eye, ShoppingCart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  Open: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
  Designing: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  Printing: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  "Post Printing": { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  Drying: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "Ready to Dispatch": { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-200" },
  "Out For Delivery": { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  Delivered: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  "Cancelled by Customer": { bg: "bg-red-50", text: "text-red-600", border: "border-red-200" },
  "Cancelled by Staff": { bg: "bg-red-50", text: "text-red-600", border: "border-red-200" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, { headers: getAuthHeaders(), withCredentials: true });
        setOrders(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const allStatuses = ["All", "Open", "Designing", "Printing", "Post Printing", "Drying", "Ready to Dispatch", "Out For Delivery", "Delivered", "Cancelled by Customer", "Cancelled by Staff"];

  const filtered = orders.filter((o) => {
    const matchSearch = !search || o.customer_name?.toLowerCase().includes(search.toLowerCase()) || o.customer_email?.toLowerCase().includes(search.toLowerCase()) || o.service?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "All" || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="page-orders" className="min-h-screen bg-[#F4F3EF]">
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap">
        <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Orders</h1>
      </div>

      <div className="px-5 sm:px-8 py-6 space-y-6">
        {/* Status filter tabs - scrollable */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allStatuses.map((s) => {
            const count = s === "All" ? orders.length : orders.filter(o => o.status === s).length;
            return (
              <button key={s} onClick={() => setFilterStatus(s)} data-testid={`order-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex-shrink-0 px-4 py-2 text-xs font-bold border transition-colors ${filterStatus === s ? "bg-[#161616] text-white border-[#161616]" : "bg-white text-[#5A5955] border-[#D9D7D0] hover:border-[#161616]"}`}>
                {s} ({count})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5955]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or service..."
            data-testid="orders-search" className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D9D7D0] text-sm focus:border-[#161616] outline-none transition-colors" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <ShoppingCart size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No orders found. Orders are auto-created when leads are approved.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#D9D7D0] overflow-x-auto">
            <table className="w-full text-left" data-testid="orders-table">
              <thead>
                <tr className="border-b border-[#D9D7D0]">
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Customer</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Service</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Amount</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Created</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const sc = statusColors[order.status] || statusColors.Designing;
                  return (
                    <tr key={order.id} className="border-b border-[#D9D7D0] hover:bg-[#F7F6F2] transition-colors" data-testid={`order-row-${order.id}`}>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-[#161616]">{order.customer_name}</p>
                        <p className="text-xs text-[#5A5955]">{order.customer_email}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{order.service || "—"}</td>
                      <td className="px-6 py-4 text-sm font-medium text-[#161616]">
                        {order.quotation ? `${parseFloat(order.quotation.grand_total).toFixed(2)}` : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-bold ${sc.bg} ${sc.text} border ${sc.border}`}>{order.status}</span>
                      </td>
                      <td className="px-6 py-4 text-xs text-[#5A5955]">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <button onClick={() => navigate(`/admin/orders/${order.id}`)} data-testid={`view-order-${order.id}`} className="p-2 hover:bg-[#F4F3EF] transition-colors" title="View">
                          <Eye size={16} className="text-[#5A5955]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
